import type { JSX } from 'react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loading } from '../components/ui.js';
import { pickSharedUrl, названиеИзПоделиться, разобратьВставленнуюСтроку } from '../lib/card-from-page.js';

// Приёмник системных ссылок исторически владел этим разбором, и по имени
// страницы его ищут тесты. Сам разбор переехал в библиотеку — им пользуется
// ещё и форма, куда ту же строку вставляют руками.
export { pickSharedUrl };

/**
 * Приём ссылки, которой поделились из другого приложения.
 *
 * Главный сценарий продукта в одно движение: человек листает Ozon или
 * Wildberries в их приложении, видит нужную вещь, жмёт «Поделиться» и выбирает
 * Загадарь. Система открывает это приложение по адресу /share-target?...,
 * а мы перехватываем ссылку и уводим человека сразу к добавлению желания.
 *
 * Сама страница ничего не показывает, кроме мгновенной заглушки: её задача —
 * достать ссылку из параметров и перенаправить дальше. Разные системы кладут
 * ссылку в разные поля (иногда прямо в text, иногда в url), поэтому проверяем
 * оба и на всякий случай вытаскиваем ссылку из текста.
 */

export function ShareTargetPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const shared = pickSharedUrl({
      url: params.get('url'),
      text: params.get('text'),
      title: params.get('title'),
    });

    // Ссылку кладём в sessionStorage, а не только в состояние перехода.
    // Причина: если человек ещё не вошёл, его сначала уведёт на страницу входа,
    // и состояние перехода потеряется. sessionStorage переживает вход, и главная
    // подхватит ссылку сразу после авторизации.
    /*
     * Заголовок, которым поделился телефон, — это почти всегда название товара.
     *
     * Раньше мы его выбрасывали и оставляли серверу одну голую ссылку. Для
     * закрытых площадок это значило пустое поле: сервер до страницы не доходит,
     * а больше названию взяться неоткуда. Теперь оно приезжает с телефона
     * бесплатно и без установки чего-либо.
     */
    /*
     * Ссылки из названия вычищаем: Android часто не заполняет title вовсе,
     * и тогда единственным источником остаётся text — а в нём и адрес товара,
     * и приглашение установить приложение. Без чистки в поле «Что это»
     * приезжала строка вида «Чайник Bosch https://shop.ru/p/1 Скачать
     * приложение: https://apps.apple.com/…».
     */
    const изТекста = (params.get('title') ?? params.get('text') ?? '').replace(/https?:\/\/[^\s<>"']+/gi, ' ');
    const название = названиеИзПоделиться(изТекста);

    /*
     * Цену телефон присылает там же и так же бесплатно.
     *
     * Авито делится тремя строками — название, цена, ссылка. На закрытой
     * площадке это единственная цена, которую мы вообще узнаем: сервер до
     * страницы не дойдёт. Разбираем тем же кодом, что и вставку в поле ссылки,
     * — иначе одно и то же желание выходило бы разным в зависимости от того,
     * поделились им или вставили руками.
     */
    const разобрано = разобратьВставленнуюСтроку(
      [params.get('title'), params.get('text'), params.get('url')].filter(Boolean).join('\n'),
    );

    const естьЧтоПоложить = Boolean(название) || разобрано?.ценаМинор !== undefined;
    if (shared && естьЧтоПоложить) {
      try {
        sessionStorage.setItem(
          'wishare:prefilled-card',
          JSON.stringify({
            url: shared,
            ...(название ? { title: название } : {}),
            ...(разобрано?.ценаМинор !== undefined ? { priceMinor: разобрано.ценаМинор } : {}),
            ...(разобрано?.валюта ? { currency: разобрано.валюта } : {}),
          }),
        );
      } catch {
        /* приватный режим без хранилища */
      }
    }

    if (shared) {
      try {
        sessionStorage.setItem('wishare:shared-url', shared);
      } catch {
        /* приватный режим без хранилища — тогда пойдём через состояние перехода */
      }
    }

    // Уводим на главную и просим её открыть форму добавления с этой ссылкой.
    // replace: чтобы кнопка «назад» не возвращала на пустую страницу-приёмник.
    navigate('/', {
      replace: true,
      state: shared ? { sharedUrl: shared } : undefined,
    });
  }, [params, navigate]);

  return <Loading label="Добавляем желание…" />;
}

